import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Select} from 'primeng/select';
import {
  CustomerUserControllerService,
  ProjectRoleType,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
} from '@frontend/shared-api';
import {TranslocoPipe} from '@jsverse/transloco';
import {RoleCheckService} from '../../../../../../../../shared/services/role-check.service';
import {finalize} from 'rxjs';
import {AVERAGE_USER_EMAIL} from '../../../../../../../../shared/constants/contributor.constants';

interface ContributorOption {
  label: string;
  value: string;
  userId: number;
}

/**
 * Step-9-5 specific contributor dropdown:
 * - Contributors should be able to switch between contributors and "average results"
 * - Invitation actions must stay PM-only (handled by StepDotMenu)
 */
@Component({
  selector: 'app-evaluation-contributor-filter-dropdown',
  imports: [FormsModule, Select, TranslocoPipe],
  templateUrl: './evaluation-contributor-filter-dropdown.component.html',
})
export class EvaluationContributorFilterDropdownComponent {
  readonly projectId = input.required<number>();
  readonly allowCustomerAdmin = input(true);
  readonly showAverageOption = input(true);
  readonly compact = input(false);

  readonly contributorSelected = output<string>();

  private readonly projectUserController = inject(ProjectUserControllerService);
  private readonly customerUserController = inject(CustomerUserControllerService);
  private readonly roleCheckService = inject(RoleCheckService);

  protected readonly loading = signal(false);
  protected readonly contributors = signal<ProjectUserDetailResponse[]>([]);
  protected readonly currentUserEmail = signal<string | undefined>(undefined);
  protected readonly selectedContributorEmail = signal<string | undefined>(undefined);

  protected readonly shouldShowDropdown = computed(() => {
    const email = this.currentUserEmail();
    const contributors = this.contributors();
    if (!email) return false;

    if (this.allowCustomerAdmin() && this.roleCheckService.hasCustomerAdminRole()) {
      return true;
    }

    const currentUser = contributors.find((c) => c.email === email);
    if (!currentUser) return false;

    // In this step, contributors should also see the dropdown.
    return (
      currentUser.roles.includes(ProjectRoleType.PROJECT_MANAGER) ||
      currentUser.roles.includes(ProjectRoleType.CONTRIBUTOR)
    );
  });

  protected readonly isProjectManagerOrAdmin = computed(() => {
    const email = this.currentUserEmail();
    const contributors = this.contributors();
    if (!email) return false;

    const currentUser = contributors.find((c) => c.email === email);
    if (!currentUser) return this.roleCheckService.hasCustomerAdminRole();

    return (
      currentUser.roles.includes(ProjectRoleType.PROJECT_MANAGER) ||
      this.roleCheckService.hasCustomerAdminRole()
    );
  });

  protected readonly contributorOptions = computed((): ContributorOption[] => {
    const allContributors = this.contributors();
    const isPMOrAdmin = this.isProjectManagerOrAdmin();

    const activeContributors = allContributors.filter(
      (user) => user.status === 'ACTIVE' || user.status === 'INVITED',
    );

    const hasRole = (user: ProjectUserDetailResponse, role: ProjectRoleType): boolean => {
      return user.roles.includes(role);
    };

    const filteredContributors = isPMOrAdmin
      ? activeContributors
      : activeContributors.filter(
          (user) =>
            hasRole(user, ProjectRoleType.CONTRIBUTOR) ||
            hasRole(user, ProjectRoleType.PROJECT_MANAGER),
        );

    const options = filteredContributors.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.email,
      userId: user.userId,
    }));

    // In step-9-5, everyone who can see the dropdown can also see the average results.
    if (this.showAverageOption()) {
      options.unshift({
        label: 'project.steps.contributor.averageResults',
        value: AVERAGE_USER_EMAIL,
        userId: -1,
      });
    }

    return options;
  });

  constructor() {
    effect(() => {
      if (this.projectId()) {
        this.loadContributors();
      }
    });
  }

  private loadContributors(): void {
    this.loading.set(true);

    this.customerUserController.getCurrentCustomerUser().subscribe({
      next: (currentUser) => {
        this.currentUserEmail.set(currentUser.email);

        this.projectUserController
          .getProjectUsersWithDetails({projectId: this.projectId()})
          .pipe(finalize(() => this.loading.set(false)))
          .subscribe({
            next: (users) => {
              this.contributors.set(users);
              if (!this.selectedContributorEmail() && currentUser.email) {
                this.selectedContributorEmail.set(currentUser.email);
                this.contributorSelected.emit(currentUser.email);
              }
            },
          });
      },
    });
  }

  protected onContributorChange(value: string | undefined): void {
    this.selectedContributorEmail.set(value);
    if (value) {
      this.contributorSelected.emit(value);
    }
  }
}
